/**
 * SoloPuzzleRoyalePanel
 *
 * ENTRY surface for SOLO Puzzle Royale, rendered inline in the Fun Challenges
 * lobby's Solo tab. Unlike multiplayer royale there is NO lobby / roster /
 * share code / opponent — it's a 1-player race. The player picks Topics, Time,
 * and Punish, then taps **Start game**: we create + auto-start a solo room in
 * one shot (useRoyaleSocket.startSolo), the royale store flips to
 * lobby→in_battle, and the global listener navigates to PuzzleRoyaleBattle
 * (which renders in its solo layout: no waiting room, straight to 3-2-1-Go).
 *
 * Difficulty is intentionally ABSENT — solo always serves the server's ramping
 * rating ladder (see royaleConstants.SOLO_LADDER). Settings the player sees:
 * Topics (optional) · Time · Punish (thresholds 3/5/10, tighter than the
 * multiplayer 5/10).
 */

import React, {useState} from 'react';
import {ActivityIndicator, Pressable, StyleSheet, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import SlideModal from '@/Atoms/SlideModal';
import {FontSizes, vs} from '@/Utils/Dimensions';

import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useRoyaleSocket from '@/Hooks/FunChallengesSocket/useRoyaleSocket';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';

import {DEFAULT_ROYALE_SETTINGS} from './royaleConstants';
import type {RoyaleSettings as RoyaleSettingsValue} from './royaleTypes';
import RoyaleSettings from './components/RoyaleSettings';

const TEAL = '#3DAB9E';
const TEAL_DARK = '#1F6C63';
const TEAL_TINT_SOFT = 'rgba(61, 171, 158, 0.06)';
const TEXT_DARK = '#262322';
const TEXT_MID = '#4D3F37';
const TEXT_MUTED = '#8A7E5F';
const YELLOW = '#FACF47';
const YELLOW_DARK = '#A9873B';
const BORDER = 'rgba(38, 35, 34, 0.10)';
const BORDER_STRONG = 'rgba(38, 35, 34, 0.18)';

export default function SoloPuzzleRoyalePanel() {
  const username = useLoggedInUserStore(
    s =>
      s.userData?.username ||
      s.userData?.name ||
      s.userData?.first_name ||
      s.email ||
      '',
  );
  const myIcon = useLoggedInUserStore(s => s.icon);

  const {ready, startSolo} = useRoyaleSocket();

  const errorMessage = useFunChallengesRoyaleStore(s => s.errorMessage);
  const clearError = useFunChallengesRoyaleStore(s => s.clearError);

  // Solo defaults from the shared royale defaults; `solo:true` marks the wire
  // path. Difficulty is carried but unused server-side for solo (the ladder
  // ignores it), so we leave the default — the settings UI hides the picker.
  const [settings, setSettings] = useState<RoyaleSettingsValue>({
    ...DEFAULT_ROYALE_SETTINGS,
    solo: true,
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Gate the button after tap until the store confirms a lobby/session exists
  // (the global listener then navigates away). Prevents a double-fire.
  const starting = useFunChallengesRoyaleStore(
    s => s.status === 'lobby' || s.status === 'in_battle',
  );

  const topicsChosen = settings.topics.length > 0;

  const handleStart = () => {
    startSolo({
      settings: {...settings, solo: true},
      creatorName: username,
      creatorAvatarUrl: typeof myIcon === 'string' ? myIcon : undefined,
    });
    // startSolo creates + auto-starts the room; the store flips to
    // lobby→in_battle and the global listener navigates to PuzzleRoyaleBattle.
  };

  if (starting) {
    return (
      <View style={[styles.panel, styles.connectingPanel]}>
        <ActivityIndicator size="large" color={TEAL_DARK} />
        <CCText style={styles.connectingTitle}>Starting your game…</CCText>
        <CCText style={styles.connectingHint}>Get ready — 3, 2, 1…</CCText>
      </View>
    );
  }

  return (
    <View style={styles.panel}>
      {!!errorMessage && (
        <Pressable onPress={clearError} style={styles.errorBanner}>
          <CCText style={styles.errorBannerText}>{errorMessage}</CCText>
          <CCText style={styles.errorBannerDismiss}>Dismiss</CCText>
        </Pressable>
      )}

      <Box style={styles.card}>
        <CCText style={styles.cardTitle}>Solve as many as you can</CCText>
        <CCText style={styles.createNote}>
          A solo race against the clock. Puzzles get harder as you go. Pick your
          settings and start whenever you're ready.
        </CCText>

        <Pressable
          onPress={() => setSettingsOpen(true)}
          style={({pressed}) => [
            styles.settingsCard,
            pressed && styles.settingsCardPressed,
          ]}>
          <View style={styles.settingsChips}>
            <Chip
              icon="tag-multiple"
              text={
                topicsChosen
                  ? `${settings.topics.length} topic${
                      settings.topics.length === 1 ? '' : 's'
                    }`
                  : 'Any topic'
              }
            />
            <Chip icon="timer-outline" text={`${settings.timerSec}s`} raw />
            <Chip
              icon="alert-octagon-outline"
              text={
                settings.punish
                  ? `Punish ${settings.punishThreshold ?? 3}`
                  : 'No punish'
              }
            />
          </View>
          <CCText style={styles.settingsHint}>Tap to edit ›</CCText>
        </Pressable>

        <PrimaryButton
          label={!ready ? 'Connecting…' : 'Start game'}
          disabled={!ready}
          onPress={handleStart}
        />
      </Box>

      <SlideModal
        visible={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        modalHeaderTitle="Solo settings"
        type="center"
        contentPadding={20}>
        <RoyaleSettings value={settings} onChange={setSettings} solo />
      </SlideModal>
    </View>
  );
}

function Chip({
  text,
  icon,
  raw,
}: {
  text: string;
  icon?: string;
  /** Skip capitalize (e.g. "120s" must not render as "120S"). */
  raw?: boolean;
}) {
  return (
    <View style={styles.chip}>
      {icon ? (
        <MaterialCommunityIcons
          name={icon}
          size={13}
          color={TEAL_DARK}
          style={styles.chipIcon}
        />
      ) : null}
      <CCText style={[styles.chipText, raw && styles.chipTextRaw]}>
        {text}
      </CCText>
    </View>
  );
}

function PrimaryButton({
  label,
  disabled,
  onPress,
}: {
  label: string;
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      style={({pressed}) => [
        styles.primaryBtn,
        disabled && styles.primaryBtnDisabled,
        !disabled && pressed && styles.primaryBtnPressed,
      ]}>
      <CCText
        style={[
          styles.primaryBtnText,
          disabled && styles.primaryBtnTextDisabled,
        ]}>
        {label}
      </CCText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  panel: {gap: 18, width: '100%'},
  connectingPanel: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: vs(40),
    gap: 8,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    backgroundColor: 'rgba(205,44,57,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(205,44,57,0.25)',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  errorBannerText: {
    flex: 1,
    fontSize: FontSizes[13] || 13,
    fontWeight: '600',
    color: '#A3242E',
  },
  errorBannerDismiss: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: '#A3242E',
    textDecorationLine: 'underline',
  },
  connectingTitle: {
    fontSize: FontSizes[16] || 16,
    fontWeight: '700',
    color: TEXT_MID,
    textAlign: 'center',
  },
  connectingHint: {
    fontSize: FontSizes[13] || 13,
    color: TEXT_MUTED,
    textAlign: 'center',
  },
  card: {
    padding: 18,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.White,
    gap: vs(14),
    shadowColor: '#4D3F37',
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 3},
  },
  cardTitle: {
    fontSize: FontSizes[16] || 16,
    fontWeight: '800',
    color: TEXT_DARK,
    letterSpacing: -0.2,
  },
  settingsCard: {
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: TEAL_TINT_SOFT,
    gap: 10,
  },
  settingsCardPressed: {borderColor: BORDER_STRONG},
  settingsChips: {flexDirection: 'row', gap: 8, flexWrap: 'wrap'},
  settingsHint: {
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.30)',
  },
  chipIcon: {marginTop: 0.5},
  chipText: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEAL_DARK,
    textTransform: 'capitalize',
  },
  chipTextRaw: {textTransform: 'none'},
  createNote: {
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
    lineHeight: 17,
  },
  primaryBtn: {
    backgroundColor: YELLOW,
    borderRadius: 14,
    borderBottomWidth: 4,
    borderColor: YELLOW_DARK,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnPressed: {borderBottomWidth: 2, paddingVertical: 18},
  primaryBtnDisabled: {backgroundColor: '#F2E8C7', borderColor: '#D9C796'},
  primaryBtnText: {
    fontSize: FontSizes[15] || 15,
    fontWeight: '800',
    color: TEXT_DARK,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  primaryBtnTextDisabled: {color: TEXT_MUTED},
});
